import { useEffect } from 'react';

// Web Vitals tracking for performance monitoring
const usePerformanceMonitoring = () => {
  useEffect(() => {
    const ENABLE_ANALYTICS = import.meta.env.VITE_ENABLE_ANALYTICS === 'true';

    if (!ENABLE_ANALYTICS) {
      return;
    }

    // Load web-vitals library dynamically
    import('https://unpkg.com/web-vitals@3/dist/web-vitals.iife.js')
      .then((webVitals) => {
        const sendToAnalytics = (metric) => {
          // Send to Google Analytics
          if (window.gtag) {
            window.gtag('event', metric.name, {
              event_category: 'Web Vitals',
              value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
              event_label: metric.id,
              non_interaction: true,
            });
          }

          // Send to Sentry
          if (window.Sentry) {
            window.Sentry.captureMessage(`Web Vital: ${metric.name}`, {
              level: 'info',
              tags: {
                vital: metric.name,
              },
              extra: {
                value: metric.value,
                id: metric.id,
                rating: metric.rating,
              },
            });
          }

          // Log to console in development
          if (import.meta.env.DEV) {
            console.log(`[Performance] ${metric.name}:`, metric);
          }
        };

        // Track Core Web Vitals
        webVitals.onCLS(sendToAnalytics); // Cumulative Layout Shift
        webVitals.onFID(sendToAnalytics); // First Input Delay
        webVitals.onLCP(sendToAnalytics); // Largest Contentful Paint
        webVitals.onFCP(sendToAnalytics); // First Contentful Paint
        webVitals.onTTFB(sendToAnalytics); // Time to First Byte
        webVitals.onINP(sendToAnalytics); // Interaction to Next Paint
      })
      .catch((error) => {
        console.error('Failed to load web-vitals:', error);
      });

    // Track page load performance
    if (window.performance && window.performance.timing) {
      window.addEventListener('load', () => {
        setTimeout(() => {
          const perfData = window.performance.timing;
          const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
          const connectTime = perfData.responseEnd - perfData.requestStart;
          const renderTime = perfData.domComplete - perfData.domLoading;

          if (window.gtag) {
            window.gtag('event', 'timing_complete', {
              name: 'page_load',
              value: pageLoadTime,
              event_category: 'Performance',
            });
          }

          if (import.meta.env.DEV) {
            console.log('Performance Metrics:', {
              pageLoadTime: `${pageLoadTime}ms`,
              connectTime: `${connectTime}ms`,
              renderTime: `${renderTime}ms`,
            });
          }
        }, 0);
      });
    }

    // Monitor long tasks (tasks > 50ms)
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.duration > 50) {
              if (import.meta.env.DEV) {
                console.warn('Long task detected:', entry);
              }
              if (window.gtag) {
                window.gtag('event', 'long_task', {
                  event_category: 'Performance',
                  value: Math.round(entry.duration),
                  non_interaction: true,
                });
              }
            }
          }
        });
        observer.observe({ entryTypes: ['longtask'] });

        return () => {
          observer.disconnect();
        };
      } catch (e) {
        console.error('PerformanceObserver not supported:', e);
      }
    }
  }, []);
};

export default usePerformanceMonitoring;

// Function to manually track custom performance metrics
export const trackCustomMetric = (metricName, value, category = 'Custom') => {
  if (window.gtag) {
    window.gtag('event', 'timing_complete', {
      name: metricName,
      value: Math.round(value),
      event_category: category,
    });
  }

  if (import.meta.env.DEV) {
    console.log(`[Custom Metric] ${metricName}:`, value);
  }
};

// Function to track resource loading times
export const trackResourceTiming = () => {
  if (window.performance && window.performance.getEntriesByType) {
    const resources = window.performance.getEntriesByType('resource');
    const slowResources = resources.filter(resource => resource.duration > 1000);

    if (slowResources.length > 0 && import.meta.env.DEV) {
      console.warn('Slow resources detected:', slowResources);
    }

    return {
      total: resources.length,
      slow: slowResources.length,
      resources: slowResources.map(r => ({
        name: r.name,
        duration: r.duration,
        type: r.initiatorType,
      })),
    };
  }
};
